import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { SignInActions } from '../base/authentication/SignInActions';

export type Initial<T> = T | (() => T);
export type LocalStorageValue<T> = [T, Dispatch<SetStateAction<T>>];

//type SetStateAction<S> = S | ((prevState: S) => S);

export const makeLocalStorageHook =
    (key: string) =>
    <T>(initialValue: Initial<T>) =>
        useTenantLocalStorage<T>(key, initialValue);

export function useTenantLocalStorage<T>(key: string, initialValue: Initial<T>) {
    return useLocalStorage(`${SignInActions.getTenant()}-${key}`, initialValue);
}

export function useLocalStorage<T>(key: string, initialValue: Initial<T>): LocalStorageValue<T> {
    const [prevKey, setPrevKey] = useState<string>(key);

    const [localStorageValue, setLocalStorageValue] = useState<T>(() => {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : isFunction(initialValue) ? initialValue() : initialValue;
    });

    const setLocalStorageContent = useCallback(
        (value: SetStateAction<T>) => {
            const store = (value: T | undefined) => {
                if (value === undefined) {
                    localStorage.removeItem(key);
                } else {
                    try {
                        localStorage.setItem(key, JSON.stringify(value));
                    } catch (e) {
                        console.warn(`localStorage.setItem failed for key "${key}"`, e);
                    }
                }
            };

            if (isSetStateFunction(value)) {
                setLocalStorageValue((prevValue) => {
                    const result = value(prevValue);
                    store(result);
                    return result;
                });
            } else {
                store(value);
                setLocalStorageValue(value);
            }
        },
        [key]
    );

    if (key != prevKey) {
        const item = localStorage.getItem(key);
        setLocalStorageContent(item ? JSON.parse(item) : isFunction(initialValue) ? initialValue() : initialValue);
        setPrevKey(key);
    }

    return [localStorageValue, setLocalStorageContent];
}

function isFunction<T>(value: Initial<T>): value is () => T {
    return typeof value === 'function';
}

function isSetStateFunction<T>(value: SetStateAction<T>): value is (p: T) => T {
    return typeof value === 'function';
}
